import React, { useRef, useEffect } from 'react';
import { makeStyles } from 'tss-react/mui';
import EditorPrimaryActionButton, {
  EditorPrimaryActionButtonProps,
} from './EditorPrimaryActionButton';
import Tooltip from '../../Tooltip';

function debounce<T extends (...args: unknown[]) => void>(
  func: (event: MouseEvent) => void,
  timeout = 300
) {
  let timer: NodeJS.Timeout;
  return (...args: Parameters<T>) => {
    clearTimeout(timer);
    timer = setTimeout(() => {
      // @ts-ignore
      func(...args);
    }, timeout);
  };
}

const useStyles = makeStyles()(() => ({
  tooltip: {
    position: 'relative',
    left: 195,
  },
}));

/**
 * @description This component is used to show a tooltip when the user hovers over the editor primary action button.
 * when in a disabled state as CJK in particular.
 * The reason for this is to avoid prop drilling through various layers of
 * base components to get to the editor dropdown action and extending it for
 * a single use case.
 *
 */
const CJKEditorPrimaryActionButton = (
  props: EditorPrimaryActionButtonProps
) => {
  const buttonRef = useRef<HTMLDivElement>(null);
  const { classes } = useStyles();
  const [isHovering, setIsHovering] = React.useState(false);

  useEffect(() => {
    const element = buttonRef.current;
    if (!element) return;

    const handleMouseMove = debounce((event: MouseEvent) => {
      const rect = element.getBoundingClientRect();
      const rightSideThreshold = rect.left + rect.width * 0.8; // Consider right 20% as the right side
      const withinThreshold = event.clientX >= rightSideThreshold;
      setIsHovering(withinThreshold);
    }, 100);

    const handleMouseLeave = debounce(() => {
      setIsHovering(false);
    }, 100);

    element.addEventListener('mousemove', handleMouseMove);
    element.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      element.removeEventListener('mousemove', handleMouseMove);
      element.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, []);

  return (
    <Tooltip
      text={'Only single take is available for this language.'}
      classes={{ tooltip: classes.tooltip }}
      open={isHovering}
    >
      <div ref={buttonRef}>
        <EditorPrimaryActionButton {...props} />
      </div>
    </Tooltip>
  );
};

export default CJKEditorPrimaryActionButton;
